import React, { useEffect, useMemo, useState } from "react";
import { Loader2, BarChart3, Download, Printer, Calculator } from "lucide-react";
import api, { money } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import VentasUtilidad from "../components/VentasUtilidad";
import InvoiceCosting from "../components/InvoiceCosting";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const totalsStr = (t) => { const e = Object.entries(t || {}); return e.length ? e.map(([c, v]) => money(c, v)).join(" · ") : "—"; };
const AMOUNT_KEYS = ["amount", "balance", "net", "total"];

export default function Reportes() {
  const { can } = useAuth();
  const [reports, setReports] = useState([]);
  const [key, setKey] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [costingInv, setCostingInv] = useState(null);

  useEffect(() => { api.get("/reports/meta").then((r) => { setReports(r.data.reports); setKey(r.data.reports[0]?.key || ""); }).catch(() => {}); }, []);

  const current = useMemo(() => reports.find((r) => r.key === key), [reports, key]);
  const isPendCost = current?.key === "pendientes_costear";
  const canCost = can("invoices:create");
  const openCost = async (id) => {
    if (!id) return;
    try { const r = await api.get(`/invoices/${id}`); setCostingInv(r.data); }
    catch { toast.error("No se pudo abrir el costeo"); }
  };

  const run = () => {
    if (!key) return;
    setLoading(true);
    const p = new URLSearchParams();
    if (from && to) { p.set("date_from", from); p.set("date_to", to); }
    const qs = p.toString() ? `?${p}` : "";
    const url = current?.custom ? `/reports/ventas-utilidad${qs}` : `/reports/${key}${qs}`;
    api.get(url).then((r) => setData(r.data)).catch(() => toast.error("No se pudo generar el reporte")).finally(() => setLoading(false));
  };
  useEffect(() => { if (key) run(); }, [key]); // eslint-disable-line

  const isAmount = (k) => AMOUNT_KEYS.includes(k);
  const cell = (row, col) => isAmount(col.key) ? money(row.currency || "GTQ", row[col.key] || 0) : (row[col.key] ?? "—");

  const exportCsv = () => {
    if (!data) return;
    const header = data.columns.map((c) => c.label).join(",");
    const lines = data.rows.map((r) => data.columns.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${data.key}.csv`; a.click();
  };

  const printReport = () => {
    if (!data) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const rows = data.rows.map((r) => `<tr>${data.columns.map((c) => `<td style="padding:6px 8px;border-bottom:1px solid #eee;${isAmount(c.key) ? "text-align:right;font-family:monospace" : ""}">${isAmount(c.key) ? esc(money(r.currency || "GTQ", r[c.key] || 0)) : esc(r[c.key] ?? "")}</td>`).join("")}</tr>`).join("");
    const head = data.columns.map((c) => `<th style="padding:6px 8px;text-align:${isAmount(c.key) ? "right" : "left"};border-bottom:2px solid #F26A00;text-transform:uppercase;font-size:11px">${esc(c.label)}</th>`).join("");
    const html = `<html><head><title>${esc(data.label)}</title></head><body style="font-family:Arial,sans-serif;color:#1f2937;padding:24px">
      <h2 style="color:#F26A00;margin:0">SELEC — ${esc(data.label)}</h2>
      <p style="color:#6b7280;font-size:12px">${data.ranged && from && to ? `Periodo: ${esc(from)} a ${esc(to)}` : "Periodo: mes actual"} · Generado ${esc(new Date().toLocaleString())}</p>
      <table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>
      <p style="margin-top:16px;font-weight:bold">Totales: ${esc(totalsStr(data.totals))}</p>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    w.location.href = url;
    setTimeout(() => { w.focus(); w.print(); URL.revokeObjectURL(url); }, 400);
  };

  return (
    <div className="space-y-5">
      <div><h1 className="text-2xl font-heading font-bold">Centro de Reportes</h1>
        <p className="text-sm text-muted-foreground">Consulta y exporta reportes administrativos y financieros.</p></div>

      <Card className="rounded-sm border shadow-none p-4 flex flex-col lg:flex-row lg:items-end gap-3" data-testid="rep-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Reporte</label>
          <Select value={key} onValueChange={(v) => { setData(null); setKey(v); }}><SelectTrigger className="rounded-sm mt-1" data-testid="rep-select"><SelectValue /></SelectTrigger>
            <SelectContent>{reports.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select>
        </div>
        {current?.ranged && (
          <>
            <div><label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Desde</label><Input type="date" className="rounded-sm mt-1 h-9 w-40 text-xs" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="rep-from" /></div>
            <div><label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Hasta</label><Input type="date" className="rounded-sm mt-1 h-9 w-40 text-xs" value={to} onChange={(e) => setTo(e.target.value)} data-testid="rep-to" /></div>
          </>
        )}
        <Button className="rounded-sm h-9 text-xs font-bold" onClick={run} data-testid="rep-run">Generar</Button>
        <Button variant="outline" className="rounded-sm h-9 text-xs gap-1" onClick={exportCsv} disabled={!data || current?.custom} data-testid="rep-csv"><Download className="h-3.5 w-3.5" /> CSV</Button>
        <Button variant="outline" className="rounded-sm h-9 text-xs gap-1" onClick={printReport} disabled={!data || current?.custom} data-testid="rep-print"><Printer className="h-3.5 w-3.5" /> Imprimir</Button>
      </Card>

      {current?.custom && !loading && <p className="text-xs text-muted-foreground -mt-2">Este reporte usa el costeo de facturas. Gastos fijos = servicios + nómina; gastos variables = gastos operativos + caja chica. Las compras de inventario ya están dentro del costo de ventas.</p>}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
        data && current?.custom && data.kpis ? <VentasUtilidad data={data} from={from} to={to} /> :
        data && data.columns && (
        <Card className="rounded-sm border shadow-none overflow-hidden" data-testid="rep-result">
          <div className="p-4 flex items-center justify-between border-b border-border">
            <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /><span className="font-heading font-bold text-sm">{data.label}</span><span className="text-xs text-muted-foreground">· {data.count} fila(s)</span></div>
            <span className="text-sm font-bold">Totales: {totalsStr(data.totals)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rep-table">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{data.columns.map((c) => <th key={c.key} className={`p-3 font-bold ${isAmount(c.key) ? "text-right" : "text-left"}`}>{c.label}</th>)}{isPendCost && canCost && <th className="p-3 font-bold text-right">Acción</th>}</tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? <tr><td colSpan={data.columns.length + (isPendCost && canCost ? 1 : 0)} className="p-10 text-center text-muted-foreground">Sin datos para el periodo.</td></tr> :
                  data.rows.map((r, i) => (
                    <tr key={i} className="border-t border-border hover:bg-muted/30">
                      {data.columns.map((c) => <td key={c.key} className={`p-3 ${isAmount(c.key) ? "text-right font-mono" : ""}`}>{cell(r, c)}</td>)}
                      {isPendCost && canCost && <td className="p-3 text-right"><Button size="sm" className="rounded-sm h-7 text-xs gap-1" onClick={() => openCost(r.id)} data-testid={`rep-costear-${r.number}`}><Calculator className="h-3.5 w-3.5" /> Costear</Button></td>}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {costingInv && <InvoiceCosting invoice={costingInv} onClose={() => setCostingInv(null)} onSaved={() => { setCostingInv(null); run(); }} />}
    </div>
  );
}
